import { ArrowRight, ChevronDown } from 'lucide-react';
import { C } from '../constants';
import PartyButton from '../components/PartyButton';
import Glass from '../components/Glass';

export default function VotingScreen({ stateData, district, setDistrict, party, setParty, onBack, onCast, error }) {
  const { parties, districts, localFont, accent, strings } = stateData;
  const canCast = !!district && !!party;

  return (
    <main className="px-5 md:px-10 py-10 max-w-5xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Step 01 of 02 · Ballot
      </div>
      <h2 style={{
        fontWeight: 700, fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
        lineHeight: 1.05, letterSpacing: '-0.02em',
        marginTop: '0.5rem', color: C.white,
      }}>
        Make it count.
        <span style={{
          fontFamily: localFont,
          fontSize: '0.5em', color: C.textDim, marginLeft: 12, fontWeight: 500,
        }}>
          {strings.yourVote}
        </span>
      </h2>

      {/* District selector */}
      <div className="mt-8">
        <label style={{
          fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase',
          color: district ? C.textDim : C.white, fontWeight: 600, display: 'block',
          transition: 'color 220ms ease',
        }}>
          Your district ·{' '}
          <span style={{ fontFamily: localFont, textTransform: 'none', letterSpacing: 0 }}>
            {strings.pickDistrict}
          </span>
        </label>
        <div className="relative mt-2 max-w-md">
          <select
            value={district}
            onChange={e => setDistrict(e.target.value)}
            className="w-full appearance-none cursor-pointer"
            style={{
              background:           '#ffffff',
              color:                district ? '#0D0D1A' : '#6B7280',
              border:               `2px solid ${district ? accent : 'transparent'}`,
              padding:              '0.95rem 1.1rem',
              paddingRight:         '2.6rem',
              fontFamily:           "'Poppins', sans-serif",
              fontSize:             '1rem',
              fontWeight:           600,
              outline:              'none',
              borderRadius:         14,
              boxShadow:            district
                ? `0 0 0 3px ${accent}30, 0 0 20px ${accent}20`
                : '0 4px 16px rgba(0,0,0,0.25)',
              transition:           'border-color 220ms ease, box-shadow 220ms ease',
            }}
          >
            <option value="" style={{ background: '#1a1a2e', color: '#9CA3AF' }}>
              — Select district / {strings.selectHint} —
            </option>
            {districts.map(d => (
              <option key={d.en} value={d.en} style={{ background: '#1a1a2e', color: '#ffffff' }}>
                {d.en}  ·  {d.local}
              </option>
            ))}
          </select>
          <ChevronDown
            size={18} color={district ? accent : '#6B7280'}
            style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          />
        </div>
      </div>

      {/* Party grid */}
      <div className="mt-9">
        <label style={{
          fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase',
          color: C.textDim, fontWeight: 600, display: 'block',
        }}>
          Your party ·{' '}
          <span style={{ fontFamily: localFont, textTransform: 'none', letterSpacing: 0 }}>
            {strings.pickParty}
          </span>
        </label>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
          {parties.map(p => (
            <PartyButton
              key={p.id}
              party={p}
              localFont={localFont}
              selected={party === p.id}
              onClick={() => setParty(p.id)}
            />
          ))}
        </div>
      </div>

      {error && (
        <Glass style={{ marginTop: '1.5rem', padding: '0.75rem 1rem', border: `1px solid ${C.pink}55` }}>
          <span style={{ color: C.white, fontSize: '0.85rem' }}>{error}</span>
        </Glass>
      )}

      {/* Action bar */}
      <div
        className="mt-10 pt-6 flex items-center justify-between flex-wrap gap-4"
        style={{ borderTop: `1px solid ${C.border}` }}
      >
        <button
          onClick={onBack}
          style={{
            background: 'transparent', border: 'none',
            color: C.textDim, fontSize: '0.9rem', cursor: 'pointer',
            fontStyle: 'italic', fontFamily: "'Poppins', sans-serif",
          }}
        >
          ← Back
        </button>

        <button
          disabled={!canCast}
          onClick={onCast}
          className="inline-flex items-center gap-3"
          style={{
            background:    canCast ? '#D32F2F' : 'rgba(255,255,255,0.05)',
            color:         canCast ? '#ffffff' : C.textDimmer,
            padding:       '0.85rem 1.8rem',
            fontWeight:    500,
            fontSize:      '0.95rem',
            cursor:        canCast ? 'pointer' : 'not-allowed',
            border:        canCast ? 'none' : `1px solid ${C.border}`,
            borderRadius:  20,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            boxShadow:     canCast
              ? '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)'
              : 'none',
            transition:    'box-shadow 200ms ease, background 200ms ease',
            fontFamily:    "'Poppins', sans-serif",
          }}
          onMouseEnter={e => {
            if (!canCast) return;
            e.currentTarget.style.background = '#C62828';
            e.currentTarget.style.boxShadow = '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)';
          }}
          onMouseLeave={e => {
            if (!canCast) return;
            e.currentTarget.style.background = '#D32F2F';
            e.currentTarget.style.boxShadow = '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)';
          }}
          onMouseDown={e => {
            if (!canCast) return;
            e.currentTarget.style.background = '#B71C1C';
            e.currentTarget.style.boxShadow = '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)';
          }}
          onMouseUp={e => {
            if (!canCast) return;
            e.currentTarget.style.background = '#C62828';
            e.currentTarget.style.boxShadow = '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)';
          }}
        >
          Cast vote
          <span style={{
            fontFamily:  localFont,
            fontSize:    '0.92rem',
            fontWeight:  500,
            opacity:     0.85,
            borderLeft:  canCast ? '1px solid rgba(255,255,255,0.3)' : `1px solid ${C.border}`,
            paddingLeft: 12,
          }}>
            {strings.castVote}
          </span>
          <ArrowRight size={18} />
        </button>
      </div>
    </main>
  );
}
